import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag, RefreshCw, X, Lock, LogOut, Loader2, AlertCircle,
  Download, FileText, BarChart3, Package, Settings, Camera, MessageSquare, Bell, Sparkles, TrendingUp, Activity,
} from 'lucide-react';
import { Button } from './ui/button';
import { DEFAULT_COSTS, getCostForItem, getStatusInfo, formatDate, formatCurrency, formatCPF, formatCNPJ, formatPhone } from './admin/adminHelpers';
import AdminDashboard from './admin/AdminDashboard';
import AdminPedidos from './admin/AdminPedidos';
import AdminCustos from './admin/AdminCustos';
import AdminIntelbrasPricing from './admin/AdminIntelbrasPricing';
import AdminChatbot from './admin/AdminChatbot';
import AdminSeo from './admin/AdminSeo';
import AdminSalesReport from './admin/AdminSalesReport';
import AdminAbandonedCarts from './admin/AdminAbandonedCarts';
import AdminMetrics from './admin/AdminMetrics';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminOrders = ({ isOpen, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [costConfig, setCostConfig] = useState(DEFAULT_COSTS);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // ── Preços Intelbras ────────────────────────────────────────────────
  const [intelbrasPricing, setIntelbrasPricing] = useState(null);
  const [pricingSaveLoading, setPricingSaveLoading] = useState(false);
  const [pricingSaveMsg, setPricingSaveMsg] = useState('');

  const fetchIntelbrasPricing = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/intelbras-pricing`);
      if (res.ok) setIntelbrasPricing(await res.json());
    } catch (err) { console.error('Erro ao buscar preços Intelbras:', err); }
  }, []);

  const saveIntelbrasPricing = async () => {
    setPricingSaveLoading(true); setPricingSaveMsg('');
    try {
      const res = await fetch(
        `${API_URL}/api/admin/intelbras-pricing`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionStorage.getItem('admin_pwd') || ''}` }, body: JSON.stringify(intelbrasPricing) }
      );
      setPricingSaveMsg(res.ok ? '✅ Preços salvos com sucesso!' : '❌ Erro ao salvar preços.');
    } catch { setPricingSaveMsg('❌ Erro de conexão.'); }
    finally { setPricingSaveLoading(false); setTimeout(() => setPricingSaveMsg(''), 4000); }
  };

  useEffect(() => {
    if (sessionStorage.getItem('admin_authenticated') === 'true') setIsAuthenticated(true);
    const saved = localStorage.getItem('admin_cost_config');
    if (saved) setCostConfig(JSON.parse(saved));
  }, []);

  // Processa retorno do login Google (Emergent Auth): #session_id=...
  const googleProcessed = useRef(false);
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.includes('session_id=') || googleProcessed.current) return;
    googleProcessed.current = true;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    (async () => {
      setLoginLoading(true); setLoginError('');
      try {
        const res = await fetch(`${API_URL}/api/auth/admin/session`, {
          method: 'POST',
          headers: { 'X-Session-ID': sessionId },
          credentials: 'include',
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Falha no login Google');
        sessionStorage.setItem('admin_authenticated', 'true');
        sessionStorage.setItem('admin_pwd', data.session_token);
        sessionStorage.setItem('admin_google_user', JSON.stringify({ email: data.email, name: data.name, picture: data.picture }));
        setIsAuthenticated(true);
      } catch (e) { setLoginError(e.message); }
      finally { setLoginLoading(false); }
    })();
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginLoading(true); setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const data = await res.json();
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_authenticated', 'true');
        sessionStorage.setItem('admin_pwd', data.session_token);
        setPassword('');
      } else {
        const data = await res.json();
        setLoginError(data.detail || 'Senha incorreta');
      }
    } catch { setLoginError('Erro ao conectar com o servidor'); }
    finally { setLoginLoading(false); }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/orders?limit=200`, {
        headers: { 'Authorization': `Bearer ${pwd}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar pedidos');
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (isOpen && isAuthenticated) { fetchOrders(); fetchIntelbrasPricing(); }
  }, [isOpen, isAuthenticated, fetchOrders, fetchIntelbrasPricing]);

  // ── Polling: notificações de novos pedidos (a cada 30s) ──────────────
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;

    // Marcar timestamp inicial — só notifica pedidos novos a partir de agora
    let lastCheck = sessionStorage.getItem('admin_last_check') || new Date().toISOString();
    sessionStorage.setItem('admin_last_check', lastCheck);

    const checkNewOrders = async () => {
      try {
        const pwd = sessionStorage.getItem('admin_pwd') || '';
        const res = await fetch(
          `${API_URL}/api/admin/orders-since?since=${encodeURIComponent(lastCheck)}`,
          { headers: { 'Authorization': `Bearer ${pwd}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.count > 0) {
          data.orders.forEach(o => {
            const cliente = o.customer_name || 'Novo cliente';
            const valor = o.amount_paid || o.total || 0;
            const ref = o.external_reference || o.id?.slice(0, 8) || '';
            toast.success(`🛒 Novo pedido #${ref}`, {
              description: `${cliente} · R$ ${valor.toFixed(2).replace('.', ',')}`,
              duration: 8000,
              action: {
                label: 'Ver',
                onClick: () => { setActiveTab('orders'); fetchOrders(); },
              },
            });
          });
          // Som de notificação (Web Audio simples)
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = 880;
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
            osc.start(); osc.stop(ctx.currentTime + 0.4);
          } catch { /* silent */ }
          // Atualizar lista
          fetchOrders();
        }
        lastCheck = data.server_time;
        sessionStorage.setItem('admin_last_check', lastCheck);
      } catch (err) {
        // Silent — polling não deve poluir UI
      }
    };

    const interval = setInterval(checkNewOrders, 30_000);
    return () => clearInterval(interval);
  }, [isOpen, isAuthenticated, fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    setActionLoading(orderId);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${pwd}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        await fetchOrders();
        if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, payment_status: newStatus }));
      }
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const deleteOrder = async (orderId) => {
    setActionLoading(orderId);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${pwd}` },
      });
      if (res.ok) {
        setOrders(prev => prev.filter(o => o.id !== orderId));
        setDeleteConfirm(null);
        if (selectedOrder?.id === orderId) setSelectedOrder(null);
      }
    } catch (err) { console.error(err); }
    finally { setActionLoading(null); }
  };

  const exportCSV = () => {
    const approved = orders.filter(o => o.payment_status === 'approved');
    const licensed = orders.filter(o => o.payment_status === 'license_generated');
    const revenue  = [...approved, ...licensed].reduce((s, o) => s + (o.amount_paid || o.total || 0), 0);
    const filteredOrders = orders.filter(o => {
      if (filter === 'all') return true;
      if (filter === 'approved') return o.payment_status === 'approved';
      if (filter === 'pending') return !o.payment_status || o.payment_status === 'pending';
      if (filter === 'license_generated') return o.payment_status === 'license_generated';
      if (filter === 'cancelled') return ['rejected', 'cancelled', 'refunded'].includes(o.payment_status);
      return true;
    });
    const headers = ['Referência','Data','Status','Cliente','CPF/CNPJ','Email','Celular','WhatsApp','Cidade/UF','Itens','Valor Venda','Custo Licença','Lucro','Licença Expira'];
    const rows = filteredOrders.map(o => {
      const c = o.customer || {}; const addr = c.endereco || {};
      const totalSale = o.amount_paid || o.total || 0;
      const totalItemCost = (o.items || []).reduce((s, i) => s + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0);
      const si = getStatusInfo(o.payment_status);
      const expDate = o.license_expires_at ? new Date(o.license_expires_at).toLocaleDateString('pt-BR') : '';
      return [
        o.external_reference || o.id?.slice(0, 8) || '', formatDate(o.created_at), si.label,
        c.nome_completo || c.razao_social || o.customer_name || '',
        c.cpf ? formatCPF(c.cpf) : c.cnpj ? formatCNPJ(c.cnpj) : o.customer_identifier || '',
        c.email || o.customer_email || '', formatPhone(c.celular || ''), formatPhone(c.whatsapp || o.customer_whatsapp || ''),
        addr.cidade && addr.estado ? `${addr.cidade}/${addr.estado}` : '',
        (o.items || []).map(i => i.name).join(' + '), totalSale.toFixed(2), totalItemCost.toFixed(2),
        (totalSale - totalItemCost).toFixed(2), expDate,
      ];
    });
    const csv = '\uFEFF' + [headers.join(';'), ...rows.map(r => r.map(v => `"${v}"`).join(';'))].join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = `pedidos_fernando_tech_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    void revenue; // used in old PDF header — kept for parity
  };

  const exportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const approved = orders.filter(o => o.payment_status === 'approved');
    const licensed = orders.filter(o => o.payment_status === 'license_generated');
    const revenue  = [...approved, ...licensed].reduce((s, o) => s + (o.amount_paid || o.total || 0), 0);
    const filteredOrders = orders.filter(o => {
      if (filter === 'all') return true;
      if (filter === 'approved') return o.payment_status === 'approved';
      if (filter === 'pending') return !o.payment_status || o.payment_status === 'pending';
      if (filter === 'license_generated') return o.payment_status === 'license_generated';
      if (filter === 'cancelled') return ['rejected', 'cancelled', 'refunded'].includes(o.payment_status);
      return true;
    });
    const doc = new jsPDF('landscape', 'mm', 'a4');
    doc.setFontSize(18); doc.text('Fernando Tech - Relatório de Pedidos', 14, 22);
    doc.setFontSize(10); doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} | Total: ${filteredOrders.length} pedidos | Faturamento: ${formatCurrency(revenue)}`, 14, 30);
    autoTable(doc, {
      startY: 36,
      head: [['Ref','Data','Status','Cliente','CPF/CNPJ','Email','Contato','Itens','Venda','Custo','Lucro']],
      body: filteredOrders.map(o => {
        const c = o.customer || {};
        const totalSale = o.amount_paid || o.total || 0;
        const totalItemCost = (o.items || []).reduce((s, i) => s + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0);
        return [
          o.external_reference || '-', formatDate(o.created_at), getStatusInfo(o.payment_status).label,
          c.nome_completo || c.razao_social || o.customer_name || '-',
          c.cpf ? formatCPF(c.cpf) : c.cnpj ? formatCNPJ(c.cnpj) : '-',
          c.email || o.customer_email || '-', formatPhone(c.celular || c.whatsapp || ''),
          (o.items || []).map(i => i.name).join(', '),
          formatCurrency(totalSale), formatCurrency(totalItemCost), formatCurrency(totalSale - totalItemCost),
        ];
      }),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [14, 165, 233], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [241, 245, 249] },
    });
    doc.save(`pedidos_fernando_tech_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportOrderPDF = async (order) => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const c = order.customer || {}; const addr = c.endereco || {};
    let y = 20;
    doc.setFontSize(20); doc.setTextColor(14, 165, 233); doc.text('Fernando Tech', 14, y);
    doc.setFontSize(10); doc.setTextColor(100); doc.text('Soluções em TI | (16) 99137-8312', 14, y + 7); y += 20;
    doc.setDrawColor(14, 165, 233); doc.setLineWidth(0.5); doc.line(14, y, 196, y); y += 10;
    doc.setFontSize(14); doc.setTextColor(30); doc.text('Detalhes do Pedido', 14, y); y += 10;
    const addField = (label, value) => {
      doc.setFontSize(9); doc.setTextColor(100); doc.text(label, 14, y);
      doc.setFontSize(10); doc.setTextColor(30); doc.text(String(value || '-'), 65, y); y += 7;
    };
    addField('Referência:', order.external_reference || '-');
    addField('Data:', formatDate(order.created_at));
    addField('Status:', getStatusInfo(order.payment_status).label);
    addField('ID Pagamento:', order.payment_id || '-');
    addField('Método:', order.payment_method || '-'); y += 5;
    doc.setFontSize(14); doc.setTextColor(30); doc.text('Dados do Cliente', 14, y); y += 10;
    addField('Tipo:', c.person_type === 'cpf' ? 'Pessoa Física' : c.person_type === 'cnpj' ? 'Pessoa Jurídica' : '-');
    if (c.person_type === 'cpf') { addField('CPF:', formatCPF(c.cpf)); addField('Nome:', c.nome_completo || '-'); }
    else if (c.person_type === 'cnpj') {
      addField('CNPJ:', formatCNPJ(c.cnpj)); addField('Razão Social:', c.razao_social || '-');
      if (c.nome_fantasia) addField('Nome Fantasia:', c.nome_fantasia);
    }
    addField('Email:', c.email || order.customer_email || '-');
    addField('Celular:', formatPhone(c.celular || '')); addField('WhatsApp:', formatPhone(c.whatsapp || order.customer_whatsapp || ''));
    if (addr.logradouro) {
      addField('Endereço:', `${addr.logradouro}, ${addr.numero || 'S/N'}${addr.complemento ? ' - ' + addr.complemento : ''}`);
      addField('Cidade/UF:', `${addr.bairro || ''} - ${addr.cidade || ''}/${addr.estado || ''} - CEP: ${addr.cep || ''}`);
    }
    y += 5;
    doc.setFontSize(14); doc.setTextColor(30); doc.text('Itens do Pedido', 14, y); y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Produto','Qtd','Preço Venda','Custo Licença','Lucro']],
      body: (order.items || []).map(item => {
        const cost = getCostForItem(item.name, costConfig);
        return [item.name, String(item.quantity || 1), formatCurrency(item.price), formatCurrency(cost), formatCurrency(item.price - cost)];
      }),
      styles: { fontSize: 9 }, headStyles: { fillColor: [14, 165, 233], textColor: 255 },
    });
    y = doc.lastAutoTable.finalY + 10;
    const totalSale = order.amount_paid || order.total || 0;
    const totalItemCost = (order.items || []).reduce((s, i) => s + getCostForItem(i.name, costConfig) * (i.quantity || 1), 0);
    doc.setFontSize(12); doc.setTextColor(30);
    doc.text(`Total Venda: ${formatCurrency(totalSale)}`, 14, y);
    doc.text(`Custo Total: ${formatCurrency(totalItemCost)}`, 14, y + 7);
    doc.setTextColor(0, 150, 0); doc.text(`Lucro: ${formatCurrency(totalSale - totalItemCost)}`, 14, y + 14);
    doc.save(`pedido_${order.external_reference || order.id?.slice(0, 8)}.pdf`);
  };

  if (!isOpen) return null;

  // ── Login Screen ─────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/20 p-2 rounded-lg"><Lock className="w-6 h-6 text-cyan-400" /></div>
              <div><h2 className="text-xl font-bold text-white">Área Restrita</h2><p className="text-sm text-slate-400">Painel Administrativo</p></div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleLogin} className="p-6">
            <button type="button" onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-slate-800 font-semibold py-3 rounded-xl transition-colors mb-4"
              data-testid="admin-google-login-btn">
              <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.4 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
              Entrar com Google
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-slate-700" /><span className="text-slate-500 text-xs">ou com senha</span><div className="flex-1 h-px bg-slate-700" />
            </div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Senha de Acesso</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Digite a senha..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 mb-4" autoFocus />
            {loginError && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{loginError}</div>}
            <Button type="submit" disabled={loginLoading || !password} className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-3">
              {loginLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</> : <><Lock className="w-4 h-4 mr-2" />Entrar</>}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main Panel ───────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 md:px-6 py-3 border-b border-slate-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-cyan-500/20 p-2 rounded-lg"><ShoppingBag className="w-5 h-5 text-cyan-400" /></div>
            <div><h2 className="text-lg font-bold text-white">Painel Administrativo</h2><p className="text-xs text-slate-400">Gerenciar vendas e licenças</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={exportCSV} variant="outline" size="sm" className="border-green-600/50 text-green-400 hover:bg-green-900/30 hidden md:flex"><Download className="w-4 h-4 mr-1" />CSV</Button>
            <Button onClick={exportPDF} variant="outline" size="sm" className="border-blue-600/50 text-blue-400 hover:bg-blue-900/30 hidden md:flex"><FileText className="w-4 h-4 mr-1" />PDF</Button>
            <Button onClick={fetchOrders} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            <Button onClick={async () => {
              const tok = sessionStorage.getItem('admin_pwd');
              sessionStorage.removeItem('admin_authenticated');
              sessionStorage.removeItem('admin_pwd');
              sessionStorage.removeItem('admin_google_user');
              setIsAuthenticated(false);
              try { await fetch(`${API_URL}/api/auth/admin/logout`, { method: 'POST', headers: { 'Authorization': `Bearer ${tok}` }, credentials: 'include' }); } catch { /* silent */ }
            }} variant="outline" size="sm" className="border-red-600/50 text-red-400 hover:bg-red-900/30" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /></Button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 py-2 border-b border-slate-700 flex gap-2 flex-wrap shrink-0">
          {[
            { id: 'dashboard', label: 'Dashboard',       icon: BarChart3 },
            { id: 'orders',    label: 'Pedidos',          icon: Package   },
            { id: 'vendas',    label: 'Vendas',           icon: TrendingUp },
            { id: 'carrinhos', label: 'Carrinhos',        icon: ShoppingBag },
            { id: 'metricas',  label: 'Métricas',         icon: Activity  },
            { id: 'chatbot',   label: 'Chatbot IA',       icon: MessageSquare },
            { id: 'seo',       label: 'SEO',              icon: Sparkles  },
            { id: 'costs',     label: 'Custos',           icon: Settings  },
            { id: 'intelbras', label: 'Preços Intelbras', icon: Camera    },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${activeTab === tab.id ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50' : 'text-slate-400 hover:text-white'}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-900/20 border border-emerald-700/40 rounded-full">
            <Bell className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
            <span className="text-[11px] text-emerald-300 font-medium">Notificações ativas</span>
          </div>
          <Button onClick={exportCSV} variant="outline" size="sm" className="border-green-600/50 text-green-400 hover:bg-green-900/30 md:hidden text-xs">CSV</Button>
          <Button onClick={exportPDF} variant="outline" size="sm" className="border-blue-600/50 text-blue-400 hover:bg-blue-900/30 md:hidden text-xs">PDF</Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && <AdminDashboard orders={orders} costConfig={costConfig} />}

        {activeTab === 'chatbot' && <AdminChatbot />}

        {activeTab === 'vendas' && <AdminSalesReport />}

        {activeTab === 'carrinhos' && <AdminAbandonedCarts />}

        {activeTab === 'metricas' && <AdminMetrics />}

        {activeTab === 'seo' && <AdminSeo />}

        {activeTab === 'costs' && (
          <AdminCustos costConfig={costConfig} setCostConfig={setCostConfig} />
        )}

        {activeTab === 'intelbras' && (
          <AdminIntelbrasPricing
            pricing={intelbrasPricing}
            onChange={setIntelbrasPricing}
            onSave={saveIntelbrasPricing}
            saveLoading={pricingSaveLoading}
            saveMsg={pricingSaveMsg}
          />
        )}

        {activeTab === 'orders' && (
          <AdminPedidos
            orders={orders} loading={loading} error={error}
            filter={filter} setFilter={setFilter}
            selectedOrder={selectedOrder} setSelectedOrder={setSelectedOrder}
            deleteConfirm={deleteConfirm} setDeleteConfirm={setDeleteConfirm}
            actionLoading={actionLoading} costConfig={costConfig}
            updateStatus={updateStatus} deleteOrder={deleteOrder}
            exportOrderPDF={exportOrderPDF}
          />
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
